import type { Project, ProjectDetails } from './api';

/** Required fields for submit — must match backend MVP_REQUIRED_FIELDS and wizard UI. */
export type MvpFieldKey =
  | 'project_title'
  | 'problem'
  | 'audience_type'
  | 'niche'
  | 'what_done'
  | 'project_status'
  | 'dev_time';

export const MVP_REQUIRED_FIELDS: MvpFieldKey[] = [
  'project_title',
  'problem',
  'audience_type',
  'niche',
  'what_done',
  'project_status',
  'dev_time',
];

const PROBLEM_MIN_LENGTH = 80;

function isFilledValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

export function isMvpFieldFilled(details: ProjectDetails | null, key: MvpFieldKey): boolean {
  const answers = details?.answers ?? {};
  if (key === 'problem') {
    const raw = (answers as Record<string, unknown>)[key];
    if (raw === null || raw === undefined) return false;
    const s = typeof raw === 'string' ? raw : String(raw);
    return s.trim().length >= PROBLEM_MIN_LENGTH;
  }
  return isFilledValue((answers as Record<string, unknown>)[key]);
}

export function calcMvpProgressFromDetails(details: ProjectDetails | null): {
  filled: number;
  total: number;
  percent: number;
} {
  const total = MVP_REQUIRED_FIELDS.length;
  const filled = MVP_REQUIRED_FIELDS.reduce((acc, key) => acc + (isMvpFieldFilled(details, key) ? 1 : 0), 0);
  const percent = total > 0 ? Math.round((filled / total) * 100) : 0;
  return { filled, total, percent };
}

export function getNextMvpField(details: ProjectDetails | null): MvpFieldKey | null {
  for (const key of MVP_REQUIRED_FIELDS) {
    if (!isMvpFieldFilled(details, key)) {
      return key;
    }
  }
  return null;
}

export function calcMvpPercentFromListItem(project: Project): number {
  const missing = new Set(project.missing_fields || []);
  const total = MVP_REQUIRED_FIELDS.length;
  const missingMvp = MVP_REQUIRED_FIELDS.filter((key) => missing.has(key)).length;
  const filled = Math.max(0, total - missingMvp);
  return total > 0 ? Math.round((filled / total) * 100) : 0;
}
